import React, { useState, useMemo } from "react";

import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

const PHOTO_DATE_SUFFIXES = [
   /_\d{4}-\d{2}-\d{2}$/,
   /_\d{4}-\d{2}_\d{2}$/,
   /_\d{8}_\d{4}$/,
   /_\d{8}$/,
   /_\d{4}_[\w-]+$/,
   /-\d{4}$/,
   /_\d{4}$/,
];

const PHOTO_DATE_PATTERNS = [
   {
      regex: /_(\d{4})-(\d{2})-(\d{2})(?:\.[^.]+)?$/,
      parse: (match) => new Date(+match[1], +match[2] - 1, +match[3]),
   },
   {
      regex: /_(\d{4})-(\d{2})_(\d{2})(?:\.[^.]+)?$/,
      parse: (match) => new Date(+match[1], +match[2] - 1, +match[3]),
   },
   {
      regex: /_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(?:\.[^.]+)?$/,
      parse: (match) =>
         new Date(+match[1], +match[2] - 1, +match[3], +match[4], +match[5]),
   },
   {
      regex: /_(\d{4})(\d{2})(\d{2})(?:\.[^.]+)?$/,
      parse: (match) => new Date(+match[1], +match[2] - 1, +match[3]),
   },
   {
      regex: /-(\d{4})(?:\.[^.]+)?$/,
      parse: (match) => new Date(+match[1], 0, 1),
   },
   {
      regex: /_(\d{4})(?:\.[^.]+)?$/,
      parse: (match) => new Date(+match[1], 0, 1),
   },
];

const getPhotoBaseName = (fileName) => {
   let baseName = fileName.replace(/\.[^.]+$/, "");

   PHOTO_DATE_SUFFIXES.forEach((suffix) => {
      baseName = baseName.replace(suffix, "");
   });

   return baseName;
};

const getPhotoDate = (fileName) => {
   for (const { regex, parse } of PHOTO_DATE_PATTERNS) {
      const match = fileName.match(regex);
      if (match) {
         const date = parse(match);
         if (!Number.isNaN(date.getTime())) {
            return date.getTime();
         }
      }
   }

   const nameWithoutExt = fileName.replace(/\.[^.]+$/, "");
   if (/_\d{4}_[\w-]+$/.test(nameWithoutExt)) {
      const years = nameWithoutExt.match(/\d{4}/g) || [];
      const recentYears = years
         .map(Number)
         .filter((year) => year >= 1990 && year <= 2100);

      if (recentYears.length) {
         return new Date(Math.max(...recentYears), 0, 1).getTime();
      }
   }

   return 0;
};

const normalizeNamePart = (value) =>
   value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z]/g, "");

const parsePlayerName = (playerName) => {
   if (!playerName) return { lastNames: [], firstNames: [] };

   const segments = playerName.split(", ");
   const lastPart = segments[0] || "";
   const firstPart = segments[1] || "";

   const lastNames = lastPart
      .split(" ")
      .map((segment) => normalizeNamePart(segment))
      .filter(Boolean);
   const firstNames = firstPart
      .split(" ")
      .map((segment) => normalizeNamePart(segment))
      .filter(Boolean);

   return { lastNames, firstNames };
};

const normalizeFilename = (fileName) =>
   normalizeNamePart(fileName.replace(/\.[^.]+$/, ""));

const levenshtein = (a, b) => {
   const matrix = Array.from({ length: a.length + 1 }, (_, i) =>
      Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
   );

   for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
         const cost = a[i - 1] === b[j - 1] ? 0 : 1;
         matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j - 1] + cost
         );
      }
   }

   return matrix[a.length][b.length];
};

const scorePhotoMatch = (playerName, fileName) => {
   const { lastNames, firstNames } = parsePlayerName(playerName);
   const normalized = normalizeFilename(getPhotoBaseName(fileName));
   let score = 0;

   [...lastNames, ...firstNames].forEach((name) => {
      if (name && normalized.includes(name)) {
         score += name.length * 10;
      }
   });

   const targets = [
      [...lastNames, ...firstNames].join(""),
      [...firstNames, ...lastNames].join(""),
   ].filter(Boolean);

   targets.forEach((target) => {
      const distance = levenshtein(target, normalized);
      const maxLen = Math.max(target.length, normalized.length, 1);
      score += (1 - distance / maxLen) * 50;
   });

   return score;
};

const getPhotoNameTokens = (fileName) =>
   getPhotoBaseName(fileName)
      .split(/[_\-.]+/)
      .map((token) => normalizeNamePart(token))
      .filter(Boolean);

const namePartsMatch = (playerPart, filePart) => {
   if (!playerPart || !filePart) return false;

   if (playerPart === filePart) return true;

   const shorter = playerPart.length <= filePart.length ? playerPart : filePart;
   const longer = shorter === playerPart ? filePart : playerPart;

   if (shorter.length >= 3 && longer.startsWith(shorter)) return true;

   const minLen = Math.min(playerPart.length, filePart.length);
   const maxLen = Math.max(playerPart.length, filePart.length);

   if (minLen >= 3 && minLen <= 4 && maxLen - minLen <= 1) {
      return levenshtein(playerPart, filePart) === 1;
   }

   return false;
};

const photoMatchesPlayerName = (playerName, fileName) => {
   const { lastNames, firstNames } = parsePlayerName(playerName);
   const tokens = getPhotoNameTokens(fileName);

   const lastMatch = lastNames.some((name) =>
      tokens.some((token) => namePartsMatch(name, token))
   );
   const firstMatch = firstNames.some((name) =>
      tokens.some((token) => namePartsMatch(name, token))
   );

   return lastMatch && firstMatch;
};

const lastNameMatchesPhoto = (lastNames, fileName) =>
   lastNames.some((name) =>
      getPhotoNameTokens(fileName).some((token) => namePartsMatch(name, token))
   );

const sortRankedPhotos = (entries) =>
   entries.sort((a, b) => {
      if (b.date !== a.date) {
         return b.date - a.date;
      }

      return b.score - a.score;
   });

const findClosestPhoto = (photos, playerName) => {
   if (!photos?.length || !playerName) return null;

   const { lastNames } = parsePlayerName(playerName);

   const rankedPhotos = photos.map((photo) => ({
      photo,
      score: scorePhotoMatch(playerName, photo.fileName),
      date: getPhotoDate(photo.fileName),
      fullMatch: photoMatchesPlayerName(playerName, photo.fileName),
      lastMatch: lastNameMatchesPhoto(lastNames, photo.fileName),
   }));

   const fullMatches = sortRankedPhotos(
      rankedPhotos.filter((entry) => entry.fullMatch)
   );
   if (fullMatches.length) {
      return fullMatches[0].photo;
   }

   const lastMatches = sortRankedPhotos(
      rankedPhotos.filter((entry) => entry.lastMatch)
   );
   if (lastMatches.length === 1) {
      return lastMatches[0].photo;
   }

   return null;
};

export default function AllPhotoSelect({
   label,
   playerId,
   playerName,
   photos,
   callback,
   enableBestMatch = false,
}) {
   const handlePhotoChange = (e) => {
      callback(e.target.value);
   };

   const [focus, setFocus] = useState(false);
   const closestPhoto = useMemo(
      () => (enableBestMatch ? findClosestPhoto(photos, playerName) : null),
      [photos, playerName, enableBestMatch]
   );

   const selectPhotos = useMemo(() => {
      const matching = photos.filter((photo) =>
         photoMatchesPlayerName(playerName, photo.fileName)
      );
      const list = matching.length > 0 ? matching : photos;

      return [...list].sort((a, b) =>
         a.fileName.toLowerCase().localeCompare(b.fileName.toLowerCase())
      );
   }, [photos, playerName]);

   return (
      <>
         {playerId && photos && !focus && (
            <Stack direction='row' spacing={1} sx={{ ml: 3 }} alignItems='center'>
               {closestPhoto && (
                  <Button
                     variant='outlined'
                     color='primary'
                     onClick={() => callback(closestPhoto.fileName)}
                  >
                     {closestPhoto.fileName}
                  </Button>
               )}
               <Button variant='outlined' onClick={() => setFocus(true)}>
                  SELECT PHOTO
               </Button>
            </Stack>
         )}

         {photos && focus && (
            <>
               <FormControl fullWidth sx={{ width: "100%" }}>
                  <InputLabel id={"photos"}>Photos</InputLabel>
                  <Select
                     label={"photos"}
                     onChange={handlePhotoChange}
                     onBlur={() => setFocus(false)}
                     sx={{ backgroundColor: "#fff" }}
                     placeholder="Select a Photo"
                  >
                     {selectPhotos.map((photo) => (
                        <MenuItem key={photo.fileName} value={photo.fileName}>
                           {photo.fileName}
                        </MenuItem>
                     ))}
          
                  </Select>
               </FormControl>
            </>
         )}
      </>
   );
}
